import LoadingScreen from '@/components/LoadingScreen';
import { useDailyActions } from "@/hooks/useDailyActions";
import { useProfile } from "@/contexts/ProfileContext";
import { useUserScores } from "@/hooks/useUserScores";
import { formatDisplayName } from "@/utils/name";
import { computeMedalTier } from "@/utils/scoring";
import { Ionicons } from "@expo/vector-icons";
import { Image } from "expo-image";
import { LinearGradient } from "expo-linear-gradient";
import { useFocusEffect, useRouter } from "expo-router";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import BronzeMedal from "../../assets/images/icons/bronze.svg";
import GoldMedal from "../../assets/images/icons/gold.svg";
import SilverMedal from "../../assets/images/icons/silver.svg";

// Pages importantes, mises en avant sous le profil (sorties de la roue dentée).
const QUICK_LINKS = [
  { labelKey: "profile.linkGifts", route: "/cadeaux" },
  { labelKey: "profile.linkPartners", route: "/partenaires" },
  { labelKey: "profile.linkAbout", route: "/about" },
  { labelKey: "profile.linkGoFurther", route: "/go-further" },
  { labelKey: "profile.linkTerms", route: "/terms-of-use" },
  { labelKey: "profile.linkPrivacy", route: "/privacy-policy" },
] as const;

export default function ProfilScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { profile, loading: profileLoading, refreshProfile } = useProfile();
  const { scores, refetch: refetchScores } = useUserScores();
  const { getCompletedActionIds, refetch: refetchDailyActions } = useDailyActions();

  // Get completed action IDs for today (sert au calcul de la médaille).
  const completedActionIds = getCompletedActionIds();

  // Refetch data when screen comes into focus — incl. le profil pour que
  // l'avatar fraîchement uploadé s'affiche au retour de l'écran d'édition.
  useFocusEffect(
    useCallback(() => {
      refreshProfile();
      refetchScores();
      refetchDailyActions();
    }, [refreshProfile, refetchScores, refetchDailyActions])
  );

  // Get real points from scores
  const totalPoints = scores?.totalPoints || 0;

  // Medal tier — based on the number of actions completed *today* (out of 9).
  // Same helper used on celebration so the medal stays in sync everywhere.
  const medalTier = computeMedalTier(completedActionIds.length);

  // Nom affiché sur la carte, au format « Prénom P. ». Vide si l'utilisateur
  // n'a pas (encore) renseigné son prénom → on affiche à la place une
  // incitation cliquable.
  const fullName = formatDisplayName(profile?.firstName, profile?.lastName);

  const handleSettingsPress = () => {
    router.push("/settings");
  };

  // Chargement en cours → loader. Profil absent alors que le chargement est
  // terminé → on n'affiche RIEN : ProfileContext vient de déconnecter et la
  // navigation renvoie vers l'écran de connexion. Afficher le loader dans ce
  // cas donnait un chargement infini, sans issue possible.
  if (profileLoading) {
    return (
      <>
        <StatusBar barStyle="light-content" />
        <LoadingScreen message={t("profile.loadingProfile")} />
      </>
    );
  }
  if (!profile) return null;

  return (
    <SafeAreaView
      style={{ flex: 1, backgroundColor: "#FBEFE3" }}
      edges={["top"]}
    >
      <StatusBar barStyle="dark-content" backgroundColor="#FBEFE3" />
      <LinearGradient
        colors={["#FBEFE3", "#FFFFFF"]}
        locations={[0, 1]}
        style={styles.container}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>{t("profile.headerTitle")}</Text>
          </View>

          {/* User Card */}
          <View>
            <LinearGradient
              colors={["#FCC189", "#CD7728"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={styles.userCard}
            >
              <View style={styles.userInfo}>
                {profile?.avatarUrl ? (
                  <Image
                    source={{ uri: profile.avatarUrl }}
                    style={styles.avatarImage}
                    cachePolicy="memory-disk"
                    contentFit="cover"
                    transition={200}
                  />
                ) : (
                  <View style={styles.avatarPlaceholder}>
                    <Ionicons name="person" size={32} color="#FFF" />
                  </View>
                )}
                <View style={styles.userDetails}>
                  {fullName ? (
                    <Text style={styles.userCardName}>{fullName}</Text>
                  ) : (
                    <TouchableOpacity
                      style={styles.addNameCta}
                      onPress={() => router.push("/edit-profile")}
                      accessibilityRole="button"
                      accessibilityLabel={t("profile.addFirstName")}
                    >
                      <Ionicons name="add-circle" size={18} color="#FFF" />
                      <Text style={styles.addNameText}>{t("profile.addFirstName")}</Text>
                    </TouchableOpacity>
                  )}
                  <Text style={styles.userCardEmail}>{profile?.email}</Text>
                  {profile?.company && (
                    <Text style={styles.userCardCompany}>{profile.company}</Text>
                  )}
                </View>
              </View>
              <TouchableOpacity
                style={styles.settingsButton}
                onPress={handleSettingsPress}
              >
                <Ionicons name="settings-outline" size={24} color="#FFF" />
              </TouchableOpacity>
            </LinearGradient>
            <View style={styles.pointsContainer}>
              <View style={styles.pointRow}>
                <View style={styles.pointHeader}>
                  <View style={styles.pointIconContainer}>
                    {medalTier === 'gold' && React.createElement(GoldMedal, { width: 24, height: 24 })}
                    {medalTier === 'silver' && React.createElement(SilverMedal, { width: 24, height: 24 })}
                    {medalTier === 'bronze' && React.createElement(BronzeMedal, { width: 24, height: 24 })}
                  </View>
                  <Text style={styles.pointLabel}>{t("profile.totalPointsLabel")}</Text>
                  <Text style={styles.pointValue}>{t("common.pointsFull", { points: totalPoints })}</Text>
                </View>
                <View style={styles.pointBarContainer}>
                  <LinearGradient
                    colors={['#A8D475', '#83BF4F']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={[
                      styles.pointBar,
                      { width: `${Math.min((totalPoints / 500) * 100, 100)}%` },
                    ]}
                  />
                </View>
              </View>

            </View>
          </View>

          {/* Quick links — pages importantes, anciennement cachées dans les réglages */}
          <View style={styles.linksSection}>
            {QUICK_LINKS.map((link, index) => (
              <TouchableOpacity
                key={link.route}
                style={[
                  styles.linkItem,
                  index === QUICK_LINKS.length - 1 && { borderBottomWidth: 0 },
                ]}
                onPress={() => router.push(link.route as any)}
              >
                <Text style={styles.linkText}>{t(link.labelKey)}</Text>
                <Ionicons name="chevron-forward" size={20} color="#CD7728" />
              </TouchableOpacity>
            ))}
          </View>

          {/* Medals Obtained */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t("profile.currentMedalTitle")}</Text>
            <View style={styles.medalsContainer}>
              <View style={[styles.medalItem, medalTier !== 'bronze' && styles.medalItemInactive]}>
                <BronzeMedal width={60} height={60} opacity={medalTier === 'bronze' ? 1 : 0.3} />
                <Text style={[styles.medalLabel, medalTier !== 'bronze' && styles.medalLabelInactive]}>
                  {t("profile.medalBronze")}
                </Text>
                <Text style={styles.medalRange}>{t("profile.medalRangeBronze")}</Text>
              </View>
              <View style={[styles.medalItem, medalTier !== 'silver' && styles.medalItemInactive]}>
                <SilverMedal width={60} height={60} opacity={medalTier === 'silver' ? 1 : 0.3} />
                <Text style={[styles.medalLabel, medalTier !== 'silver' && styles.medalLabelInactive]}>
                  {t("profile.medalSilver")}
                </Text>
                <Text style={styles.medalRange}>{t("profile.medalRangeSilver")}</Text>
              </View>
              <View style={[styles.medalItem, medalTier !== 'gold' && styles.medalItemInactive]}>
                <GoldMedal width={60} height={60} opacity={medalTier === 'gold' ? 1 : 0.3} />
                <Text style={[styles.medalLabel, medalTier !== 'gold' && styles.medalLabelInactive]}>
                  {t("profile.medalGold")}
                </Text>
                <Text style={styles.medalRange}>{t("profile.medalRangeGold")}</Text>
              </View>
            </View>
          </View>
        </ScrollView>
      </LinearGradient>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 24,
  },
  scrollContent: {
    padding: 24,
    paddingTop: 0,
    paddingBottom: 24,
  },
  linksSection: {
    marginTop: 20,
    backgroundColor: "#FFF",
    borderRadius: 16,
    paddingHorizontal: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  linkItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0, 0, 0, 0.06)",
  },
  linkText: {
    fontFamily: "Inter_500Medium",
    fontSize: 16,
    color: "#000",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#666",
    marginTop: 12,
  },
  header: {
    alignItems: "center",
    marginBottom: 24,
  },
  headerTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 20,
    color: "#000",
  },
  userCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  userInfo: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  avatarPlaceholder: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16,
  },
  avatarImage: {
    width: 60,
    height: 60,
    borderRadius: 30,
    marginRight: 16,
  },
  userDetails: {
    flex: 1,
  },
  userCardName: {
    fontFamily: "Inter_700Bold",
    fontSize: 18,
    color: "#FFF",
    marginBottom: 2,
  },
  addNameCta: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 2,
  },
  addNameText: {
    fontFamily: "Inter_700Bold",
    fontSize: 18,
    color: "#FFF",
    textDecorationLine: "underline",
  },
  userCardEmail: {
    fontFamily: "Inter_400Regular",
    fontSize: 12,
    color: "rgba(255, 255, 255, 0.8)",
  },
  userCardCompany: {
    fontFamily: "Inter_400Regular",
    fontSize: 11,
    color: "rgba(255, 255, 255, 0.7)",
    marginTop: 2,
  },
  settingsButton: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  section: {
    marginTop: 24,
  },
  sectionTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#000",
    marginBottom: 12,
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFF",
    borderRadius: 12,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  editButtonText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 16,
    color: "#8B4513",
    marginLeft: 8,
  },
  editContainer: {
    backgroundColor: "#FFF",
    borderRadius: 12,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "#000",
    marginBottom: 8,
  },
  textInput: {
    backgroundColor: "#F5F5F5",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#000",
  },
  selectInput: {
    backgroundColor: "#F5F5F5",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  selectInputText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#999",
  },
  selectInputTextSelected: {
    color: "#000",
  },
  editActions: {
    flexDirection: "row",
    marginTop: 8,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: "#F5F5F5",
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  cancelButtonText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 16,
    color: "#666",
  },
  pointsContainer: {
    backgroundColor: "#FFF",
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    paddingLeft: 16,
    paddingRight: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  pointRow: {
    paddingTop: 12,
    paddingBottom: 12,
  },
  pointHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  pointIconContainer: {
    width: 24,
    height: 24,
    marginRight: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  pointLabel: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#000",
    flex: 1,
  },
  pointBarContainer: {
    flex: 1,
    height: 8,
    backgroundColor: "#F0F0F0",
    borderRadius: 4,
    overflow: "hidden",
  },
  pointBar: {
    height: "100%",
    borderRadius: 4,
  },
  pointValue: {
    fontFamily: "Inter_700Bold",
    fontSize: 12,
    color: "#000",
    minWidth: 60,
    textAlign: "right",
  },
  objectivesContainer: {
    backgroundColor: "#FFF",
    borderRadius: 12,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  objectiveRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
  },
  objectiveIconContainer: {
    width: 24,
    height: 24,
    marginRight: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  objectiveName: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#000",
    flex: 1,
  },
  objectiveIconCompleted: {
    opacity: 0.4,
  },
  objectiveNameCompleted: {
    color: "#999",
  },
  checkmarkContainer: {
    marginLeft: 8,
  },
  noObjectivesText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
    textAlign: "center",
    paddingVertical: 8,
  },
  objectiveCheckbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#666",
    alignItems: "center",
    justifyContent: "center",
  },
  objectiveCheckboxCompleted: {
    backgroundColor: "#83BF4F",
    borderColor: "#83BF4F",
  },
  medalsContainer: {
    flexDirection: "row",
    justifyContent: "space-around",
    backgroundColor: "#FFF",
    borderRadius: 12,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  medalItem: {
    alignItems: "center",
  },
  medalItemInactive: {
    opacity: 0.5,
  },
  medalLabel: {
    fontFamily: "Inter_700Bold",
    fontSize: 14,
    color: "#000",
    marginTop: 8,
  },
  medalLabelInactive: {
    color: "#999",
  },
  medalRange: {
    fontFamily: "Inter_400Regular",
    fontSize: 11,
    color: "#CD7728",
    marginTop: 4,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
  },
  modalContent: {
    backgroundColor: "#FFF",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 20,
    paddingBottom: 40,
    maxHeight: "80%",
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#EEE",
  },
  modalTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 18,
    color: "#000",
  },
  modalItem: {
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  modalItemText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#333",
  },
});
